import React, { useState, useEffect, useRef } from 'react';
import { 
  getPrivateChatMessages, 
  sendPrivateMessage, 
  clearPrivateChatMessages, 
  deletePrivateChat,
  initializePrivateChatSession,
  acceptPrivateChat,
  rejectPrivateChat,
  markMessagesAsRead
} from '../utils/privateChatAPI';
import { 
  getMessageContentForDisplay, 
  formatMessageTimestamp,
  validateMessageContent 
} from '../utils/privateChatCrypto';
import MessageInput from './MessageInput';
import ConfirmDialog from './ConfirmDialog';
import './PrivateChatDialog.css';

const PrivateChatDialog = ({ 
  chat, 
  currentUserID, 
  userPrivateKey,
  onClose,
  onChatDeleted,
  userKeys,
  refreshTrigger
}) => {
  const [messages, setMessages] = useState([]);
  const [loading, setLoading] = useState(false);
  const [sending, setSending] = useState(false);
  const [error, setError] = useState(null);
  const [sessionKeys, setSessionKeys] = useState(null);
  const [otherUserPublicKey, setOtherUserPublicKey] = useState(null);
  const [userPublicKey, setUserPublicKey] = useState(null);
  const [firstMessage, setFirstMessage] = useState(null);
  const [showConfirmDialog, setShowConfirmDialog] = useState(false);
  const [confirmAction, setConfirmAction] = useState(null);
  const messagesEndRef = useRef(null);

  const { chatID, otherUserID, status, initiatorID } = chat;
  
  // 判断当前用户是发起方还是接收方
  const isInitiator = initiatorID === currentUserID;
  const isReceiver = !isInitiator;

  // Decode first message for pending chats
  useEffect(() => {
    const decodeFirstMessage = () => {
      if (status === 'pending' && isReceiver && chat.firstMessageEncrypted) {
        try {
          // 直接base64解码第一条消息
          const decodedMessage = decodeURIComponent(escape(atob(chat.firstMessageEncrypted)));
          setFirstMessage(decodedMessage);
        } catch (error) {
          setFirstMessage('Failed to decode first message');
        }
      }
    };

    decodeFirstMessage();
  }, [status, isReceiver, chat.firstMessageEncrypted]);

  // Initialize chat session
  useEffect(() => {
    const initializeSession = async () => {
      if (!chatID || !userPrivateKey) return;

      try {
        setLoading(true);
        setError(null);

        const session = await initializePrivateChatSession(chatID, userPrivateKey, userKeys);
        setSessionKeys(session.sessionKeys);
        setOtherUserPublicKey(session.otherUserPublicKey);
        setUserPublicKey(session.userPublicKey);
        
        // Load messages
        await loadMessages();
      } catch (err) {
        setError(err.message || 'Failed to initialize chat session');
      } finally {
        setLoading(false);
      }
    };

    initializeSession();
  }, [chatID, userPrivateKey]);

  // Load messages
  const loadMessages = async () => {
    try {
      const response = await getPrivateChatMessages(chatID, userKeys);
      if (response && response.messages) {
        setMessages(response.messages);
        
        // Mark messages as read when user views the chat
        if (status === 'established') {
          try {
            await markMessagesAsRead(chatID, userKeys);
          } catch (markReadError) {
            // Don't show error to user, this is not critical
          }
        }
      }
    } catch (err) {
      setError(err.message || 'Failed to load messages');
    }
  };

  // Refresh messages when refreshTrigger changes
  useEffect(() => {
    if (refreshTrigger > 0 && chatID) {
      loadMessages();
    }
  }, [refreshTrigger, chatID]);

  // Auto refresh messages every 3 seconds for established chats
  useEffect(() => {
    if (status === 'established' && chatID) {
      const interval = setInterval(() => {
        loadMessages();
      }, 3000);
      
      return () => clearInterval(interval);
    }
  }, [status, chatID]);

  // Scroll to bottom when messages change
  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  // Send message
  const handleSendMessage = async (messageData) => {
    if (!sessionKeys || !otherUserPublicKey || !userPublicKey) {
      setError('Chat session not properly initialized');
      return;
    }

    // Extract message content from the message data object
    const messageContent = messageData.content || messageData;
    
    if (!messageContent || typeof messageContent !== 'string' || messageContent.trim().length === 0) {
      setError('Message content is required');
      return;
    }

    try {
      setSending(true);
      setError(null);

      await sendPrivateMessage(
        chatID, 
        messageContent, 
        otherUserPublicKey, 
        userPublicKey,
        userKeys
      );

      // Reload messages to get the new one
      await loadMessages();
    } catch (err) {
      setError(err.message || 'Failed to send message');
    } finally {
      setSending(false);
    }
  };

  // Show confirmation dialog for clear messages
  const handleClearMessages = () => {
    setConfirmAction('clear');
    setShowConfirmDialog(true);
  };

  // Show confirmation dialog for delete chat
  const handleDeleteChat = () => {
    setConfirmAction('delete');
    setShowConfirmDialog(true);
  };

  // Handle confirmation dialog actions
  const handleConfirmAction = async () => {
    try {
      setLoading(true);
      setShowConfirmDialog(false);

      if (confirmAction === 'clear') {
        await clearPrivateChatMessages(chatID, userKeys);
        setMessages([]);
      } else if (confirmAction === 'delete') {
        await deletePrivateChat(chatID, userKeys);
        
        if (onChatDeleted) {
          onChatDeleted(chatID);
        }
        
        if (onClose) {
          onClose();
        }
      }
    } catch (err) {
      setError(err.message || `Failed to ${confirmAction} chat`);
    } finally {
      setLoading(false);
    }
  };

  // Handle confirmation dialog cancel
  const handleCancelAction = () => {
    setShowConfirmDialog(false);
    setConfirmAction(null);
  };

  const handleAcceptChat = async () => {
    try {
      setLoading(true);
      setError(null);

      if (!userKeys || !userKeys.publicKey) {
        throw new Error('User keys not available');
      }

      await acceptPrivateChat(chatID, userKeys.publicKey, userKeys);
      
      // Refresh the chat to get updated status
      if (onChatDeleted) {
        onChatDeleted(chatID); // This will trigger a refresh
      }
    } catch (err) {
      setError(err.message || 'Failed to accept chat');
    } finally {
      setLoading(false);
    }
  };

  const handleRejectChat = async () => {
    try {
      setLoading(true);
      setError(null);

      await rejectPrivateChat(chatID, userKeys);
      
      if (onChatDeleted) {
        onChatDeleted(chatID);
      }
      
      onClose();
    } catch (err) {
      setError(err.message || 'Failed to reject chat');
    } finally {
      setLoading(false);
    }
  };

  // Get decrypted message content
  const getDecryptedMessageContent = async (message) => {
    if (!sessionKeys) return '[Session not initialized]';

    try {
      return await getMessageContentForDisplay(
        message, 
        currentUserID, 
        sessionKeys.sessionPrivateKey
      );
    } catch (error) {
      return '[Failed to decrypt message]';
    }
  };

  if (loading && !messages.length) {
    return (
      <div className="private-chat-dialog">
        <div className="private-chat-dialog-header">
          <div className="chat-info">
            <h3>{otherUserID}</h3>
            <span className="chat-status">{status}</span>
          </div>
        </div>
        <div className="private-chat-dialog-loading">
          <div className="loading-spinner"></div>
          <span>Loading chat...</span>
        </div>
      </div>
    );
  }

  return (
    <div className="private-chat-dialog">
      <div className="private-chat-dialog-header">
        <div className="chat-info">
          <h3>{otherUserID}</h3>
          <span className="chat-status">{status}</span>
        </div>
        
        <div className="chat-actions">
          {status === 'pending' && isReceiver ? (
            // 待处理状态且是接收方：显示接受和拒绝按钮
            <>
              <button
                className="action-button accept-button"
                onClick={handleAcceptChat}
                title="Accept chat"
                disabled={loading}
              >
                ✓
              </button>
              <button
                className="action-button reject-button"
                onClick={handleRejectChat}
                title="Reject chat"
                disabled={loading}
              >
                ✗
              </button>
            </>
          ) : status === 'pending' && isInitiator ? (
            // 待处理状态且是发起方：显示删除会话按钮
            <>
              <button
                className="action-button delete-button"
                onClick={handleDeleteChat}
                title="Delete chat"
                disabled={loading}
              >
                ❌
              </button>
            </>
          ) : (
            // 已建立状态：显示清空、删除、关闭按钮
            <>
              <button
                className="action-button clear-button"
                onClick={handleClearMessages}
                title="Clear chat history"
                disabled={loading}
              >
                🗑️
              </button>
              <button
                className="action-button delete-button"
                onClick={handleDeleteChat}
                title="Delete chat"
                disabled={loading}
              >
                ❌
              </button>
            </>
          )}
        </div>
      </div>

      {error && (
        <div className="error-banner">
          <div className="error-icon">⚠️</div>
          <span>{error}</span>
          <button 
            className="dismiss-button"
            onClick={() => setError(null)}
          >
            ✕
          </button>
        </div>
      )}

      <div className="private-chat-dialog-content">
        <div className="messages-container">
          {status === 'pending' ? (
            <div className="pending-chat">
              <div className="pending-icon">⏳</div>
              {isReceiver ? (
                <>
                  <h4>Private Chat Request</h4>
                  <p>You have received a private chat request from <strong>{otherUserID}</strong></p>
                  {firstMessage && (
                    <div className="first-message-preview">
                      <h5>First Message:</h5>
                      <div className="message-content">{firstMessage}</div>
                    </div>
                  )}
                  <p>Click "Accept" to start the conversation or "Reject" to decline.</p>
                </>
              ) : (
                <>
                  <h4>Private Chat Sent</h4>
                  <p>You have sent a private chat request to <strong>{otherUserID}</strong></p>
                  <p>Waiting for their response...</p>
                </>
              )}
            </div>
          ) : messages.length > 0 ? (
            messages.map((message) => (
              <MessageItem
                key={message.message_id}
                message={message}
                currentUserID={currentUserID}
                getDecryptedContent={getDecryptedMessageContent}
              />
            ))
          ) : (
            <div className="no-messages">
              <div className="no-messages-icon">💬</div>
              <p>No messages yet</p>
              <small>Start the conversation by sending a message</small>
            </div>
          )}
          <div ref={messagesEndRef} />
        </div>
      </div>

      <div className="private-chat-dialog-footer">
        <MessageInput
          onSendMessage={handleSendMessage}
          disabled={sending || status !== 'established'}
          placeholder={status !== 'established' ? 'Chat not established' : 'Type a message...'}
          maxLength={175}
        />
      </div>
      
      {/* Confirmation Dialog */}
      <ConfirmDialog
        isOpen={showConfirmDialog}
        title={confirmAction === 'clear' ? 'Clear Chat History' : 'Delete Private Chat'}
        message={
          confirmAction === 'clear' 
            ? `Are you sure you want to clear the chat history with ${otherUserID}?`
            : `Are you sure you want to delete the private chat with ${otherUserID}? This action cannot be undone!`
        }
        confirmText="Confirm"
        cancelText="Cancel"
        type={confirmAction === 'clear' ? 'warning' : 'danger'}
        onConfirm={handleConfirmAction}
        onCancel={handleCancelAction}
      />
    </div>
  );
};

// Message Item Component
const MessageItem = ({ message, currentUserID, getDecryptedContent }) => {
  const [decryptedContent, setDecryptedContent] = useState('[Decrypting...]');
  const [isDecrypting, setIsDecrypting] = useState(true);

  useEffect(() => {
    const decryptMessage = async () => {
      try {
        const content = await getDecryptedContent(message);
        setDecryptedContent(content);
      } catch (error) {
        setDecryptedContent('[Failed to decrypt]');
      } finally {
        setIsDecrypting(false);
      }
    };

    decryptMessage();
  }, [message, getDecryptedContent]);

  const isFromCurrentUser = message.sender_id === currentUserID;

  return (
    <div className={`message-item ${isFromCurrentUser ? 'own-message' : 'other-message'}`}>
      <div className="private-chat-message-container">
        <div className="message-text">
          {isDecrypting ? (
            <span className="decrypting-indicator">Decrypting...</span>
          ) : (
            decryptedContent
          )}
        </div>
        <div className="message-meta">
          <span className="message-time">
            {formatMessageTimestamp(message.timestamp)}
          </span>
          {isFromCurrentUser && (
            <span className="message-status">
              {message.is_read ? '✓✓' : '✓'}
            </span>
          )}
        </div>
      </div>
    </div>
  );
};

export default PrivateChatDialog;
